/**
 * Ελαφριά virtualization ΚΑΙ ΣΤΟΥΣ ΔΥΟ ΑΞΟΝΕΣ για το πλέγμα του καταλόγου (§13.11).
 *
 * ΓΙΑΤΙ δικό μας και όχι βιβλιοθήκη: 200-260 γραμμές × 22 στήλες ≈ 4.400-5.700
 * κελιά, καθένα με πεδίο τιμής, διακόπτη διαθεσιμότητας και κατάσταση εκκρεμούς
 * αλλαγής — χωρίς παράθυρο η οθόνη κολλάει σε κάθε πάτημα πλήκτρου. Νέα εξάρτηση
 * npm δεν προστίθεται για ~80 γραμμές μαθηματικών (το package.json της εφαρμογής
 * δεν έχει καμία βιβλιοθήκη virtualization και δεν μπαίνει μία τώρα).
 *
 * Η υλοποίηση κρατά ΣΤΑΘΕΡΟ ύψος γραμμής και σταθερό πλάτος στήλης
 * (catalogUi.ts), οπότε η ορατή περιοχή είναι σκέτη διαίρεση — καμία μέτρηση
 * DOM ανά κελί, καμία ResizeObserver ανά γραμμή.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

export interface Viewport {
  scrollTop: number;
  scrollLeft: number;
  width: number;
  height: number;
}

export interface Range {
  start: number;
  /** ΑΠΟΚΛΕΙΣΤΙΚΟ όριο (όπως το slice). */
  end: number;
}

const EMPTY: Viewport = { scrollTop: 0, scrollLeft: 0, width: 0, height: 0 };

const same = (a: Viewport, b: Viewport): boolean =>
  a.scrollTop === b.scrollTop && a.scrollLeft === b.scrollLeft
  && a.width === b.width && a.height === b.height;

/**
 * Παρακολουθεί θέση και μέγεθος του container. Το scroll event μπαίνει σε
 * requestAnimationFrame: ένα «τράβηγμα» της ροδέλας παράγει δεκάδες events ανά
 * frame και χωρίς αυτό κάθε ένα θα ζητούσε render.
 */
export const useGridViewport = (
  ref: { current: HTMLElement | null }
): { viewport: Viewport; onScroll: () => void; remeasure: () => void } => {
  const [viewport, setViewport] = useState<Viewport>(EMPTY);
  const frame = useRef<number | null>(null);
  const latest = useRef<Viewport>(EMPTY);

  const read = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const next: Viewport = {
      scrollTop: el.scrollTop,
      scrollLeft: el.scrollLeft,
      width: el.clientWidth,
      height: el.clientHeight,
    };
    if (same(next, latest.current)) return;
    latest.current = next;
    setViewport(next);
  }, [ref]);

  const onScroll = useCallback(() => {
    if (frame.current !== null) return;
    frame.current = window.requestAnimationFrame(() => {
      frame.current = null;
      read();
    });
  }, [read]);

  useEffect(() => {
    read();
    const el = ref.current;
    // Το πλέγμα αλλάζει πλάτος όταν ανοίγει/κλείνει το καλάθι ή το πλαϊνό μενού —
    // χωρίς ResizeObserver η ορατή περιοχή έμενε στο παλιό πλάτος και οι δεξιές
    // στήλες έμεναν κενές μέχρι το επόμενο scroll.
    let observer: ResizeObserver | null = null;
    if (el && typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(() => read());
      observer.observe(el);
    } else {
      window.addEventListener('resize', read);
    }
    return () => {
      if (observer) observer.disconnect();
      else window.removeEventListener('resize', read);
      if (frame.current !== null) {
        window.cancelAnimationFrame(frame.current);
        frame.current = null;
      }
    };
  }, [ref, read]);

  return { viewport, onScroll, remeasure: read };
};

/**
 * Ποια αντικείμενα σταθερού μεγέθους φαίνονται. Το `overscan` κρατά μερικά έξω
 * από την οθόνη ώστε το γρήγορο scroll να μη δείχνει κενό.
 */
export const visibleRange = (
  offset: number,
  viewportSize: number,
  itemSize: number,
  count: number,
  overscan: number
): Range => {
  if (count <= 0 || itemSize <= 0) return { start: 0, end: 0 };
  // Πριν την πρώτη μέτρηση (viewportSize = 0) δείχνουμε ένα μικρό πρώτο κομμάτι
  // αντί για τίποτα: αλλιώς η πρώτη ζωγραφιά είναι κενό πλέγμα για ένα frame.
  const size = viewportSize > 0 ? viewportSize : itemSize * 12;
  const start = Math.max(0, Math.floor(offset / itemSize) - overscan);
  const end = Math.min(count, Math.ceil((offset + size) / itemSize) + overscan);
  return { start, end: Math.max(start, end) };
};
